interface ErrorWithResponse extends Error {
  response?: { data?: unknown; status?: number };
}

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;
const GENERIC_HTTP_MESSAGES = new Set([
  'bad request',
  'unauthorized',
  'forbidden',
  'not found',
  'conflict',
  'unprocessable entity',
  'internal server error',
  'service unavailable',
]);

const isGenericHttpMessage = (message: string): boolean =>
  GENERIC_HTTP_MESSAGES.has(message.trim().toLowerCase());

const selectMessage = (messages: Array<string | undefined>): string | undefined => {
  const firstMessage = messages.find((message): message is string => Boolean(message));
  return (
    messages.find((message): message is string => {
      if (!message) return false;
      return !isGenericHttpMessage(message);
    }) || firstMessage
  );
};

function parseMessage(value: unknown): string | undefined {
  if (isNonEmptyString(value)) {
    const trimmedValue = value.trim();
    try {
      return parseMessage(JSON.parse(trimmedValue)) ?? trimmedValue;
    } catch {
      return trimmedValue;
    }
  }

  if (Array.isArray(value)) {
    return selectMessage(value.map(parseMessage));
  }

  if (!value || typeof value !== 'object') return undefined;

  const record = value as Record<string, unknown>;
  // LJ errors have appeared as data.message, an array of error envelopes, and
  // JSON encoded in errorResponse. Prefer nested applicant-specific messages
  // over wrapper labels such as `message: "Bad Request"`.
  const candidates = [
    record.message,
    record.errorResponse,
    record.error,
    record.response,
    record.data,
  ];
  return selectMessage(candidates.map(parseMessage));
}

export function extractErrorMessage(error: unknown): string {
  const errorWithResponse = error as ErrorWithResponse;
  return (
    parseMessage(errorWithResponse.response?.data) ||
    parseMessage(error) ||
    'An unspecified error occurred.'
  );
}
